import { useNavigate } from "react-router-dom";
import { TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React, { useState } from "react";
import { useUpdatePasswordInsidePanelMutation } from "../../service/auth/api";
import { toast } from "react-toastify";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";


const ChangePassword = () => {

  const navigate = useNavigate();
  const [updatePassword, { isLoading }] = useUpdatePasswordInsidePanelMutation();

  const [passwords, setPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPasswords(prev => ({ ...prev, [id]: value }));
    if (errors[id as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [id]: "" }));
    }
  };

  const validate = () => {
    let isValid = true;
    const newErrors = { oldPassword: "", newPassword: "", confirmPassword: "" };

    // Password regex (At least 8 chars, 1 uppercase, 1 lowercase, 1 symbol)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

    if (!passwords.oldPassword) {
      newErrors.oldPassword = "Old Password is required"; // NOSONAR
      isValid = false;
    }

    if (!passwords.newPassword) {
      newErrors.newPassword = "New Password is required"; // NOSONAR
      isValid = false;
    } else if (!passwordRegex.test(passwords.newPassword)) {
      newErrors.newPassword = "Password must be at least 8 characters long, contain at least one uppercase letter, one lowercase letter, and one symbol"; // NOSONAR
      isValid = false;
    }

    if (!passwords.confirmPassword) {
      newErrors.confirmPassword = "Confirm Password is required"; // NOSONAR
      isValid = false;
    } else if (passwords.newPassword !== passwords.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"; // NOSONAR
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const payload = {
        oldPassword: passwords.oldPassword,
        newPassword: passwords.newPassword,
        isResetPassword: false
      };
      const res = await updatePassword(payload).unwrap();
      toast.success(res.message || "Password changed successfully");
      navigate("/dashboard");
    } catch (err: any) {
      console.error("Change Password Error:", err);
      toast.error(err?.data?.message || "Failed to change password");
    }
  };

  return (
    <>
      <CommonBreadcrumb
        title="Change Password"
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Manage Profile", onClick: () => navigate("/manage-profile") },
          { label: "Change Password" },
        ]}
      />


      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_33">
              <label htmlFor="oldPassword">Old Password</label>
              <TextField
                id="oldPassword"
                placeholder="Enter Old Password"
                fullWidth
                hiddenLabel
                type={showOldPassword ? "text" : "password"}
                value={passwords.oldPassword}
                onChange={handleChange}
                error={!!errors.oldPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label={showOldPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {errors.oldPassword && <p className="text_danger" style={{ marginTop: "5px", fontSize: "14px", color: "red" }}>{errors.oldPassword}</p>}
            </div>
            <div className="control_group w_33">
              <label htmlFor="newPassword">New Password</label>
              <TextField
                id="newPassword"
                placeholder="Enter New Password"
                hiddenLabel
                fullWidth
                type={showNewPassword ? "text" : "password"}
                value={passwords.newPassword}
                onChange={handleChange}
                error={!!errors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label={showNewPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {errors.newPassword && <p className="text_danger" style={{ marginTop: "5px", fontSize: "14px", color: "red" }}>{errors.newPassword}</p>}
            </div>
            <div className="control_group w_33">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <TextField
                id="confirmPassword"
                placeholder="Enter Confirm New Password"
                hiddenLabel
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                value={passwords.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {errors.confirmPassword && <p className="text_danger" style={{ marginTop: "5px", fontSize: "14px", color: "red" }}>{errors.confirmPassword}</p>}
            </div>
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" onClick={handleSave} disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
